import type { Vec2, Vec3 } from '../geo/types';
import { closestPointOnPolyline } from '../geo/polyline';

export type SoundKind =
  'water' | 'rigging' | 'cargo' | 'carpentry' | 'market' | 'bell' | 'bird' | 'footstep' | 'hooves';

export interface SoundEmitter {
  readonly id: string;
  readonly kind: SoundKind;
  readonly at: Vec3;
  readonly reach: number;
  readonly level: number;
  readonly path?: readonly Vec2[];
  readonly canvas?: boolean;
}

export interface SpatialSound {
  readonly emitter: SoundEmitter;
  readonly volume: number;
  readonly pan: number;
  readonly cutoff: number;
}

/** Distance and stereo bearing in the camera's actual world frame, including altitude. */
export function spatialSound(emitter: SoundEmitter, position: Vec3, yaw: number): SpatialSound {
  const along = emitter.path && closestPointOnPolyline(emitter.path, [position[0], -position[2]]);
  const at: Vec3 = along ? [along.point[0], emitter.at[1], -along.point[1]] : emitter.at;
  const dx = at[0] - position[0],
    dz = at[2] - position[2];
  const flat = Math.hypot(dx, dz),
    distance = Math.hypot(flat, at[1] - position[1]);
  const t = Math.max(0, 1 - distance / emitter.reach);
  const fade = emitter.kind === 'bell' ? t / (1 + distance / 180) : t * t * (3 - 2 * t);
  return {
    emitter,
    volume: emitter.level * fade,
    pan:
      flat < 0.01
        ? 0
        : Math.max(-0.9, Math.min(0.9, (dx * Math.cos(yaw) - dz * Math.sin(yaw)) / flat)),
    cutoff: 1400 + 7600 * t * t,
  };
}

export function nearestSound(
  sources: readonly SoundEmitter[],
  kind: SoundKind,
  position: Vec3,
  yaw: number
): SpatialSound | null {
  let best: SpatialSound | null = null;
  for (const source of sources) {
    if (source.kind !== kind) continue;
    const sound = spatialSound(source, position, yaw);
    if (sound.volume > 0.002 && (!best || sound.volume > best.volume)) best = sound;
  }
  return best;
}
